import React, { useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import Edit from "@mui/icons-material/Edit";
import Visibility from "@mui/icons-material/Visibility";
import { UITableProps } from "./UITableProps";
import UIChip from "../chips/UIChip";
import colors from "@/utils/colorsV2";
import UIPagination from "../pagination/UIPagination";
import UIText from "../text/UIText";
import Status, { estados } from "@/utils/processStatus";
import { Replay } from "@mui/icons-material";
import UITooltip from "../tooltip/UITooltip";
import UIMenuActions from "../menu/menu-actions/UIMenuActions";

const UITable: React.FC<UITableProps> = ({
  columns,
  rows,
  actions,
}) => {
  const rowsNumber = 20;
  const [page, setPage] = React.useState(0);
  const [rowsPerPage] = React.useState(rowsNumber);

  useEffect(() => {
    setPage(0);
  }, [rows.length]);

  const changePage = (page: number) => {
    setPage(page - 1);
  };

  // Función para determinar el tipo de chip según el estatus
  const getChipType = (idEstatus: string | number): "success" | "info" | "secondary" | "error" | "primary" | "warning" | "purple" => {
    const statusId = idEstatus?.toString() || '';
    
    // Solicitud completa (verde)
    if (statusId === Status.REGISTRATION_COMPLETE) {
      return "success";
    }
    
    // En proceso (azul)
    if (statusId === Status.VERIFICATION || 
        statusId === Status.CONTRACT_REVIEW
    ) {
      return "info";
    }
    
    // En proceso de firma (gris/secondary)
    if (
      statusId === Status.CONTRACT_REVIEW
    ) {
      return "primary";
    }
    
    // Error en sistema (rojo)
    if (
      statusId === Status.ERROR_EXTRACT_DATA ||
      statusId === Status.GUARANTOR_VALIDATION_ERROR ||
      statusId === Status.CREATING_REQUEST_ERROR ||
      statusId === Status.UPDATING_REQUEST_ERROR ||
      statusId === Status.ERROR_LEGACY ||
      statusId === Status.ERROR_GUARANTOR_CONTRACT_REVIEW ||
      statusId === Status.ERROR_IN_SIGNING ||
      statusId === Status.ERROR_AGENT_CHANGE_REQUEST ||
      statusId === Status.ERROR_GUARANTOR_CHANGE_REQUEST ||
      statusId?.toLowerCase().includes("error")
    ) {
      return "error";
    }

    if (
      statusId === Status.GUARANTOR_CONTRACT_REVIEW || 
      statusId === Status.IN_SIGNING
    ) {
      return "purple";
    }
    // Por defecto (secondary)
    return "warning";
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        position: "relative",
        height: "calc(100% - 88px)",
      }}
    >
      <Table
        stickyHeader
        sx={{
          borderColor: colors.grayLM200,
          height: "100%",
          position: "relative",
        }}
      >
        <TableHead>
          <TableRow>
            {columns.length > 0 ? (
              columns.map((column) => {
                return (
                  <TableCell
                    sx={{
                      padding: "12px 24px",
                      verticalAlign: "middle",
                      backgroundColor: colors["grayLM50"],
                      ...(["status", "actions"].includes(column.id) && {
                        minWidth: "130px",
                      }),
                    }}
                    key={column.id}
                  >
                    <UIText
                      text={column.label}
                      size="text-xs"
                      color="gray600"
                    />
                  </TableCell>
                );
              })
            ) : (
              <Typography variant="subtitle1" sx={{ color: colors.grayLM200 }}>
                No hay columnas
              </Typography>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0
            ? rows?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : rows
          )?.map((row, index) => (
            <TableRow
              sx={{
                verticalAlign: "super",
              }}
              key={index}
            >
              {columns.map((column) => {
                switch (column.id) {
                  case "empresa":
                    return (
                      <TableCell
                        size="small"
                        key={column.id}
                        sx={{ padding: "16px 24px" }}
                      >
                        {row["idSiebelAserta"] && (
                          <>
                          <UIText
                            text={`Aserta`}
                            size="text-s"
                            color="gray600"
                            weight="medium"                    
                          />
                          <UIText 
                            text={`ID ${row["idSiebelAserta"]}`} 
                            size="text-s" 
                            color="gray600" 
                            weight="light" 
                            sx={{ whiteSpace: 'nowrap' }}
                          />
                          </>
                        )}
                        {row["idSiebelInsurgentes"] && (
                          <>
                            <UIText
                              text={`Insurgentes`}
                              size="text-s"
                              color="gray600"
                              weight="medium"
                            />
                            <UIText
                              text={`ID ${row["idSiebelInsurgentes"]}`}
                              size="text-s"
                              color="gray600"
                              weight="light"
                              sx={{ whiteSpace: 'nowrap' }}
                            />
                          </>
                        )}
                      </TableCell>
                    );
                  case "nombre":
                    return (
                      <TableCell size="small" key={column.id}>
                        <UIText
                          text={row[column.id]}
                          size="text-s"
                          color="blue"
                        />
                      </TableCell>
                    );
                  case "estatus":
                    return (
                      <TableCell
                        size="small"
                        key={column.id}
                        sx={{ textAlign: "left", padding: "16px 24px" }}
                      >
                        <UIChip
                          sxText={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'clip',
                          }}
                          type={getChipType(row.idEstatus)}
                          label={row[column.id]?.toString()?.length > 25 ? row[column.id].toString().substring(0, 25) + "..." : row[column.id]?.toString()}
                        />
                      </TableCell>
                    );
                  case "proccess":
                    return (
                      <TableCell
                        size="small"
                        sx={{ textAlign: "center", padding: "16px 24px" }}
                        key={column.id}
                      >
                        {row[column.id]}
                      </TableCell>
                    );
                  case "creditLine":
                    return (
                      <TableCell
                        size="small"
                        sx={{ padding: "16px 24px" }}
                        key={column.id}
                      >
                        <p>
                          <span>$ - - - </span>
                          <br />
                          <span style={{ color: "green" }}>$ - - - </span>
                        </p>
                      </TableCell>
                    );
                  case "actions":
                    return (
                      <TableCell
                        sx={{ padding: "16px 24px", textAlign: "center" }}
                        size="small"
                        key={column.id}
                      >
                        <UIMenuActions
                          actions={actions ?? []}
                          solicitud={row}
                        />
                      </TableCell>
                    );
                  case "idEstatus":
                    return <React.Fragment key={column.id}></React.Fragment>;
                  default:
                    return (
                      <TableCell
                        sx={{ padding: "16px 24px" }}
                        size="small"
                        key={column.id}
                      >
                        <UIText
                          text={row[column.id]}
                          size="text-s"
                          color="gray600"
                        />
                      </TableCell>
                    );
                }
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows?.length > 0 && (
        <Box
          width="100%"
          display="flex"
          justifyContent="center"
          sx={{
            position: "sticky",
            bottom: "0",
            width: "100%",
            padding: "12px 24px 16px 24px",
            backgroundColor: "white",
            borderTop: `1px solid ${colors.grayLM200}`,
          }}
        >
          <UIPagination
            total={rows.length}
            perPage={rowsNumber}
            currentPage={page + 1}
            onPageChange={changePage}
            isLoading={false}
          />
        </Box>
      )}
    </TableContainer>
  );
};

export default UITable;
